// use-query-hook.ts — base helper for every query hook in the suites.
//
// Wraps React Query's useQuery to produce a QueryResult<T> per the protocol.
// See apps/fe/src/hooks/planning/async-protocol.md for the full contract.

'use client';

import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  QUERY_STATUS,
  type QueryOptions,
  type QueryResult,
  type QueryStatus,
} from '@/types/async-protocol';

export interface QueryHookOptions<TData> {
  queryKey: QueryKey;
  queryFn: (ctx: { signal: AbortSignal }) => Promise<TData>;
  options?: QueryOptions;
}

export function useQueryHook<TData, TError = unknown>(
  input: QueryHookOptions<TData>,
): QueryResult<TData, TError> {
  const { queryKey, queryFn, options } = input;

  const result = useQuery<TData, TError>({
    queryKey,
    queryFn: ({ signal }) => queryFn({ signal }),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
    refetchOnWindowFocus: options?.refetchOnFocus ?? false,
    // Undefined leaves React Query's default in place (three retries with
    // backoff) — the behavior every caller had before this option existed.
    retry: options?.retry,
  });

  // Depend on the bound `refetch` method, never on the result object: React
  // Query builds a fresh result on every render, so keying the callback on it
  // handed back a new `refetch` identity each time. That silently breaks every
  // consumer that lists `refetch` in a dependency array — the dependency never
  // compares equal, so their memo or effect re-runs on every render.
  const { refetch: runQuery } = result;
  const refetch = useCallback(() => {
    void runQuery();
  }, [runQuery]);

  const data = result.data === undefined ? null : result.data;
  const error = result.error === null || result.error === undefined ? null : result.error;

  return {
    data,
    status: mapQueryStatus(result.isError, result.isSuccess),
    error,
    refetch,
  };
}

function mapQueryStatus(isError: boolean, isSuccess: boolean): QueryStatus {
  // Three-state mapping: anything that isn't success or error is "Loading"
  // (covers fetching, refetching from a stale-while-revalidate state, and the
  // "enabled: false / idle" case where the consumer is gating the query).
  if (isError) return QUERY_STATUS.Errored;
  if (isSuccess) return QUERY_STATUS.Success;
  return QUERY_STATUS.Loading;
}
